import React from 'react'
import { cn } from '../../utils/cn'

export interface SwitchProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> {
  /**
   * Switch label
   */
  label?: string

  /**
   * Description text (below label)
   */
  description?: string

  /**
   * Size variant
   */
  size?: 'sm' | 'md' | 'lg'
}

const switchSizes = {
  sm: {
    track: 'w-8 h-4',
    thumb: 'h-3 w-3',
    translate: 'translate-x-4',
  },
  md: {
    track: 'w-11 h-6',
    thumb: 'h-5 w-5',
    translate: 'translate-x-5',
  },
  lg: {
    track: 'w-14 h-7',
    thumb: 'h-6 w-6',
    translate: 'translate-x-7',
  },
}

export const Switch = React.forwardRef<HTMLInputElement, SwitchProps>(
  (
    {
      className,
      label,
      description,
      size = 'md',
      disabled,
      checked,
      id,
      ...props
    },
    ref
  ) => {
    const switchId = id || `switch-${Math.random().toString(36).substr(2, 9)}`
    const sizes = switchSizes[size]

    return (
      <div className="flex items-start gap-3">
        <div className="relative inline-block">
          <input
            ref={ref}
            type="checkbox"
            role="switch"
            id={switchId}
            checked={checked}
            className="sr-only peer"
            disabled={disabled}
            {...props}
          />
          <label
            htmlFor={switchId}
            className={cn(
              'relative inline-flex items-center rounded-full transition-colors cursor-pointer',
              'peer-focus:ring-2 peer-focus:ring-toast-gold peer-focus:ring-offset-2',
              'peer-checked:bg-avocado-green bg-soft-tan',
              disabled && 'opacity-50 cursor-not-allowed',
              sizes.track,
              className
            )}
          >
            <span
              className={cn(
                'inline-block rounded-full bg-white transition-transform',
                'ml-0.5',
                checked && sizes.translate,
                sizes.thumb
              )}
            />
          </label>
        </div>

        {(label || description) && (
          <div className="flex flex-col gap-1">
            {label && (
              <label
                htmlFor={switchId}
                className={cn(
                  'text-sm font-medium text-deep-charcoal cursor-pointer',
                  disabled && 'opacity-50 cursor-not-allowed'
                )}
              >
                {label}
              </label>
            )}

            {description && (
              <p className={cn(
                'text-xs text-deep-charcoal/60',
                disabled && 'opacity-50'
              )}>
                {description}
              </p>
            )}
          </div>
        )}
      </div>
    )
  }
)

Switch.displayName = 'Switch'

export default Switch
